/**
 * Zoomrad super app — mini app ICHIDA to'lov (in-app payment).
 *
 * Boshqa provayderlardan farqi: Click/Payme/Paynet foydalanuvchini tashqi
 * checkout sahifasiga olib chiqadi, Zoomrad esa mobil to'lov ilovalari kabi
 * ishlaydi — foydalanuvchi o'z kartasini tanlaydi va to'lov shu yerda, API
 * orqali bajariladi (hech qanday redirect yo'q).
 *
 * Ikkala endpoint ham `Authorization: Bearer <JWT>` talab qiladi — token
 * `ZoomradLogin` dan keladi va `client.ts` uni avtomatik qo'shadi.
 */

import { toast } from "sonner";
import { apiPost } from "./client";

/** `POST /MiniApp/ZoomradGetCards` javobidagi bitta karta */
export interface ZoomradCard {
  /** To'lovda ishlatiladigan karta identifikatori */
  cardId: string;
  /** Maskalangan karta raqami (mas. "860049******1234") */
  maskedPan: string;
  /** Foydalanuvchi bergan nom (mas. "Asosiy karta") */
  cardName: string;
  /** Karta turi (mas. "uzcard" | "humo" | "visa") */
  type: string;
  /** Bank logotipi URL (bo'sh bo'lishi mumkin) */
  bankLogoUrl: string;
}

/**
 * Foydalanuvchining Zoomrad'dagi kartalari.
 * `POST /MiniApp/ZoomradGetCards` (body yo'q — server tokendan aniqlaydi).
 *
 * Javob null/noto'g'ri shaklda kelsa bo'sh massivga normalizatsiya qilinadi —
 * UI "karta yo'q" holatini ko'rsatadi, crash bo'lmaydi.
 */
export async function getZoomradCards(): Promise<ZoomradCard[]> {
  const res = await apiPost<ZoomradCard[] | null>("/MiniApp/ZoomradGetCards");
  return Array.isArray(res) ? res : [];
}

export interface ZoomradPayFeeRequest {
  /** Yig'im (fee) ID — bemor bolaning `child.id` si */
  feeId: number;
  /** So'mdagi summa (tiyin EMAS) */
  amount: number;
  /** `ZoomradCard.cardId` */
  cardId: string;
}

/**
 * To'lov muvaffaqiyatsiz bo'lganda backend standart xato shaklini (HTTP
 * status + `{ errors: { "": ["..."] } }`, mas. "To'lov amalga oshmadi
 * (Zoomrad kod: 1004)") qaytaradi — boshqa barcha endpointlar bilan bir xil
 * ApiErrorBody kontrakti. Shuning uchun asosiy xato yo'li allaqachon
 * `apiPost` → `handleApiError` (`client.ts`) orqali TO'LIQ qoplangan: u
 * xabarni ajratib toast ko'rsatadi va `ApiError` tashlaydi.
 *
 * Ba'zi backendlar shu bilan birga HTTP 200 qaytarib, muvaffaqiyatsizlikni
 * javob tanasidagi `success: false` bilan bildirishi mumkin — shu holat
 * uchun qo'shimcha (ikkinchi darajali) himoya sifatida saqlanadi.
 */
export interface ZoomradPayFeeResponse {
  success?: boolean;
  message?: string;
}

/** Envelope darajasidagi to'lov xatosi (HTTP 200, lekin `success: false`) */
export class ZoomradPaymentError extends Error {
  constructor(message = "To'lovni amalga oshirib bo'lmadi") {
    super(message);
    this.name = "ZoomradPaymentError";
  }
}

/**
 * Tanlangan karta bilan to'lovni bajaradi.
 * `POST /MiniApp/ZoomradPayFee` — `{ feeId, amount, cardId }`.
 *
 * Xatolar:
 *  - non-2xx  → `apiPost` toast ko'rsatadi va `ApiError` tashlaydi (asosiy yo'l)
 *  - envelope → shu yerda toast ko'rsatiladi va `ZoomradPaymentError` tashlanadi (fallback)
 *
 * Ya'ni chaqiruvchi faqat `catch` qilib holatini tiklashi kifoya — xabar
 * ko'rsatish API qatlamining zimmasida (`client.ts` bilan bir xil konvensiya).
 * `ChildDetailPage` shu ikkalasidan tashqari — javobga umuman yetib
 * bormaydigan tarmoq xatosi (CORS, aloqa uzilishi) uchun ham fallback
 * toast'ga ega, aks holda foydalanuvchi hech narsa ko'rmay qoladi.
 */
export async function payZoomradFee(payload: ZoomradPayFeeRequest): Promise<void> {
  const res = await apiPost<ZoomradPayFeeResponse | null>(
    "/MiniApp/ZoomradPayFee",
    payload,
  );

  if (res && res.success === false) {
    const error = new ZoomradPaymentError(res.message);
    toast.error(error.message, { duration: 6000 });
    throw error;
  }
}
